import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconPencil, IconPlus, IconSparkles } from '@tabler/icons-react';
import PageLoader from 'src/UI/loader/PageLoader';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ApiError } from 'src/error/ApiError';
import { useSiesta } from 'src/UI/app-container/SiestaProvider';
import { useAppContainer } from 'src/UI/app-container/useAppContainer';
import SkillDeleteButton from '../skill-library/SkillDeleteButton';
import UnshareSkillButton from '../skill-library/UnshareSkillButton';
import SkillEditorModal from './SkillEditorModal';
import useTenantSkills from './hooks/useTenantSkills';
import useTenantSkill from './hooks/useTenantSkill';
import useDeleteTenantSkill from './hooks/useDeleteTenantSkill';
import type { SiestaSkillListItem } from './types';

type EditorState = { mode: 'closed' } | { mode: 'create' } | { mode: 'edit'; skillId: string };

const FIND_OUT_MORE_PROMPT =
  'What are custom skills and how can you use them to help me? Give me some examples.';

export default function ManageSiestaSkillsPage() {
  const [editorState, setEditorState] = useState<EditorState>({ mode: 'closed' });
  const [skillToDelete, setSkillToDelete] = useState<SiestaSkillListItem | null>(null);

  const { data: skills, isLoading, isError } = useTenantSkills();
  const { mutateAsync: deleteSkill, isPending: isDeleting } = useDeleteTenantSkill();

  const { setSiestaMode } = useAppContainer();
  const { sendUserMessage, resetIfDisplaced } = useSiesta();

  const closeEditor = () => setEditorState({ mode: 'closed' });

  const editingSkillId = editorState.mode === 'edit' ? editorState.skillId : null;
  const { freshSkill: editingSkill } = useTenantSkill(editingSkillId, closeEditor);

  const handleAskSiesta = async () => {
    try {
      const conversationId = resetIfDisplaced();
      setSiestaMode('chat');
      await sendUserMessage(FIND_OUT_MORE_PROMPT, null, conversationId);
    } catch (error) {
      notifyAndLogError('Could not open Siesta', 'Please try again.', error);
    }
  };

  const handleConfirmDelete = async () => {
    if (!skillToDelete) return;
    try {
      await deleteSkill({ skillId: skillToDelete.id });
      setSkillToDelete(null);
    } catch (error) {
      const detail =
        error instanceof ApiError ? error.message : 'An error occurred while deleting the skill.';
      notifyAndLogError('Failed to delete skill', detail, error);
    }
  };

  return (
    <Container size="md" px={0}>
      <ConfirmDeleteModal
        opened={skillToDelete !== null}
        onClose={() => setSkillToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete skill"
        message={`Delete the '${skillToDelete?.name}' skill? This cannot be undone.`}
        isLoading={isDeleting}
      />
      <SkillEditorModal
        opened={editorState.mode !== 'closed'}
        loading={editorState.mode === 'edit' && editingSkill == null}
        skill={editorState.mode === 'edit' ? (editingSkill ?? null) : null}
        onClose={closeEditor}
      />

      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <Stack gap="xs">
            <Title order={3}>Manage Siesta skills</Title>
            <Text size="sm" c="dimmed" maw={560}>
              Skills are workflows, tasks, and reusable steps that Siesta can learn and follow to
              help you when chatting. This can be anything from running analysis, to building
              dashboards and presentations.
            </Text>
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={<IconSparkles size={16} />}
              onClick={handleAskSiesta}
              styles={{ root: { paddingInline: 0 } }}
            >
              Ask Siesta to find out more
            </Button>
          </Stack>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setEditorState({ mode: 'create' })}
          >
            Create skill
          </Button>
        </Group>

        <SkillsList
          skills={skills}
          isLoading={isLoading}
          isError={isError}
          onEdit={skillId => setEditorState({ mode: 'edit', skillId })}
          onDelete={setSkillToDelete}
        />
      </Stack>
    </Container>
  );
}

function SkillsList({
  skills,
  isLoading,
  isError,
  onEdit,
  onDelete,
}: {
  skills: SiestaSkillListItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onEdit: (skillId: string) => void;
  onDelete: (skill: SiestaSkillListItem) => void;
}) {
  if (isLoading) {
    return <PageLoader absolute={false} />;
  }

  if (isError) {
    return <Text c="red">Failed to load skills. Please refresh the page or try again later.</Text>;
  }

  if (!skills?.length) {
    return (
      <Card withBorder padding="xl" radius="md">
        <Stack gap="xs" align="center">
          <Text fw={500}>No skills yet</Text>
          <Text size="sm" c="dimmed" ta="center" maw={420}>
            You haven&apos;t created any custom skills. Create your first one, or ask Siesta in chat
            to build one with you.
          </Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap="sm">
      {skills.map(skill => (
        <Card key={skill.id} withBorder padding="md" radius="md">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={6} style={{ minWidth: 0 }}>
              <Group gap="xs" align="center">
                <Text fw={600}>{skill.name}</Text>
                {!skill.isEnabled && (
                  <Badge size="xs" variant="light" color="gray">
                    Disabled
                  </Badge>
                )}
              </Group>
              <Text size="sm" c="dimmed">
                {skill.description}
              </Text>
            </Stack>
            <Group gap="xs" wrap="nowrap">
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={`Edit ${skill.name}`}
                onClick={() => onEdit(skill.id)}
              >
                <IconPencil size={16} />
              </ActionIcon>
              {skill.isShared && <UnshareSkillButton skillId={skill.id} skillLabel={skill.name} />}
              <SkillDeleteButton
                label={skill.name}
                isShared={skill.isShared}
                onDelete={() => onDelete(skill)}
              />
            </Group>
          </Group>
        </Card>
      ))}
    </Stack>
  );
}
